
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { translations, Language } from './translations';

interface TranslationContextType {
  t: typeof translations.en;
  lang: Language;
  setLanguage: (lang: Language) => void;
  isBrazil: boolean;
  currency: string;
  currencySymbol: string;
  region: string;
  setRegion: (region: string) => void;
}

export const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const REGIONS = [
  { id: 'us', symbol: '$', lang: 'en' },
  { id: 'uk', symbol: '£', lang: 'en' },
  { id: 'za', symbol: 'R', lang: 'en' },
  { id: 'br', symbol: 'R$', lang: 'br' }
];

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [region, setRegionState] = useState(() => {
    const saved = localStorage.getItem('platapost_region');
    if (saved) return saved;

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const locale = navigator.language.toLowerCase();
      
      if (tz.includes('Sao_Paulo') || tz.includes('Brazil') || locale.includes('br')) return 'br';
      if (tz.includes('Johannesburg') || tz.includes('Cape_Town') || locale.includes('za')) return 'za';
      if (tz.includes('London') || locale.includes('gb') || locale.includes('uk')) return 'uk';
      if (tz.includes('America/') || locale.includes('us')) return 'us';
    } catch (e) {}
    
    return 'us';
  });

  const [lang, setLang] = useState<Language>(() => {
    const saved = localStorage.getItem('platapost_lang');
    if (saved === 'en' || saved === 'br') return saved;
    
    // Auto-detect based on region
    const reg = REGIONS.find(r => r.id === region);
    return (reg?.lang as Language) || 'en';
  });

  const setLanguage = useCallback((newLang: Language) => {
    setLang(newLang);
    localStorage.setItem('platapost_lang', newLang);
  }, []);

  const setRegion = useCallback((newRegion: string) => {
    setRegionState(newRegion);
    localStorage.setItem('platapost_region', newRegion);
    
    // Also update language if not explicitly set before? 
    // Actually, let's just update language to default of that region if they change region
    const reg = REGIONS.find(r => r.id === newRegion);
    if (reg) {
      setLang(reg.lang as Language);
      localStorage.setItem('platapost_lang', reg.lang);
    }
  }, []);

  const t = useMemo(() => translations[lang], [lang]);
  const isBrazil = lang === 'br';
  const currencySymbol = REGIONS.find(r => r.id === region)?.symbol || '$';

  const value = useMemo(() => ({
    t,
    lang,
    setLanguage,
    isBrazil,
    currency: currencySymbol, // Keep name stable if used elsewhere
    currencySymbol,
    region,
    setRegion
  }), [t, lang, setLanguage, isBrazil, currencySymbol, region, setRegion]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslationGlobal = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslationGlobal must be used within a TranslationProvider');
  }
  return context;
};
